import { FC, useState } from "react";

import { Typography } from "antd";
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Modal } from '@hdi-seguros/web-components.modal';

import { Improvements, InsurancePolicy, Object, RiskCoverages, Ubication } from "../../../../../../../shared/interfaces";
import { Column, _renderCard, _renderColumns } from "../../../../utils/renderSections";
import { formatDate, formattedPrice } from "../../../../../../../shared/utils";
import { CollapsableSections } from "..";
import { ColSize, RowSize } from "../../../../../../../shared/components";

interface VariousRiskPossessionsCollapsableProps {
    possessionInfo: Ubication;
    insurancePolicyDetail: InsurancePolicy;
}

const generateImprovements = ( { descripcion, vencimiento, cumplimiento, estado }: Improvements ): Array<Column> => ([
    {
        label: 'Descripción',
        value: descripcion,
        columnSize: { xs: { span: 12 }, lg: { span: 6 } }
    },
    {
        label: 'Vencimiento',
        value: formatDate( vencimiento ),
        columnSize: { xs: { span: 12 }, lg: { span: 6 } }
    },
    {
        label: 'Estado',
        value: estado,
        columnSize: { xs: { span: 12 }, lg: { span: 6 } }
    },
    {
        label: 'Cumplimiento',
        value: cumplimiento,
        columnSize: { xs: { span: 12 }, lg: { span: 6 } }
    },
])

export const VariousRiskPossessionsCollapsable: FC<VariousRiskPossessionsCollapsableProps> = ({ possessionInfo, insurancePolicyDetail }) => {

    const [showModal, setShowModal] = useState(false);
    const [objects, setObjects] = useState<Array<Array<Column>>>([])

    const openObjectsModal = ( objects: Array<Object> ) => {
        setShowModal(true);
        const objectsToShow = objects.map(({ detalle, sumaAse }): Array<Column> => ([
            { label: 'Objeto', value: detalle, columnSize: { xs: { span: 12 } } }, 
            { label: 'Suma asegurada', value: `${ insurancePolicyDetail.moneda }${ formattedPrice( sumaAse ) }`, columnSize: { xs: { span: 12 } } 
        }]))
        setObjects( objectsToShow );
    }

    const generateCoverages = ({ nombreCob, sumaCob, primCob, franquicia, objetos }: RiskCoverages, currency: string = '$'): Array<Column> => ([
        {
            label: 'Cobertura',
            value: nombreCob,
            columnSize: { xs: { span: 12 }, sm: { span: 12 }, md: { span: 6 }, lg: { span: 6 } }
        },
        {
            label: 'Suma asegurada',
            value: `${ currency } ${ formattedPrice( sumaCob ) }`,
            columnSize: { xs: { span: 12 }, sm: { span: 12 }, md: { span: 6 }, lg: { span: 6 } }
        },
        {
            label: 'Prima',
            value: `${ currency } ${ formattedPrice( primCob ) }`,
            columnSize: { xs: { span: 12 }, sm: { span: 12 }, md: { span: 6 }, lg: { span: 6 } }
        },
        {
            label: 'Franquicia',
            value: franquicia !== 'SIN FRANQUICIA' ? franquicia : 'Sin franquicia',
            columnSize: { xs: { span: 12 }, sm: { span: 12 }, md: { span: 6 }, lg: { span: 4 } }
        },
        {
            label: '',
            value: objetos.length ? <ActionButton label="Ver detalle" type="text" onClick={() => openObjectsModal( objetos )} /> : '',
            columnSize: { xs: { span: 4 }, sm: { span: 4 }, md: { span: 2 }, lg: { span: 2 } }
        },
    ]);
    

    const generalData: Array<Column> = [
        {
            label: 'Riesgo',
            value: possessionInfo.riesgo,
        },
        {
            label: 'Producto',
            value: possessionInfo.producto,
        },
        {
            label: 'Articulo de tarifa',
            value: possessionInfo.artTarifa,
        },
        {
            label: 'Suma Asegurada',
            value: formattedPrice( possessionInfo.sumaTotal ),
        },
        {
            label: 'Zona de riesgo',
            value: possessionInfo.zona,
        },
        {
            label: 'Dirección',
            value: possessionInfo.direccion,
        },
        {
            label: 'Provincia',
            value: possessionInfo.provincia,
        },
        {
            label: 'Localidad',
            value: possessionInfo.localidad,
        },
        {
            label: 'Codigo Postal',
            value: possessionInfo.codPostal,
        },
    ];

    const characteristics: Array<Column> =  [
        {
            label: 'Producto',
            value: possessionInfo.producto,
            valueInBold: true,
            columnSize: { xs: { span: 24 } }
        },
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.coberturas.map(( coverage, key ) => _renderCard( key, generateCoverages( coverage, insurancePolicyDetail?.moneda )) )
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];

    const particularClauses: Array<Column> = [
        {
            label: '',
            value: possessionInfo.clausulas.length ? possessionInfo.clausulas.map( el => el.clausula ).join(' - ') : 'No existen clásulas particulares',
            columnSize: { xs: { span: 24 } }
        }
    ];

    const improvementExpiration: Array<Column> = [
        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.mejoras.length
                            ? possessionInfo.mejoras.map(( improvement, key ) => _renderCard( key, generateImprovements( improvement )) ) 
                            : <Typography.Text> No hay vencimiento de mejoras </Typography.Text>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ];
    
    return (
        <>
            <CollapsableSections 
                title="Datos generales"
                children={  
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( generalData ) }
                    </RowSize> 
                }
            />

            <CollapsableSections 
                title="Características"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( characteristics ) }
                    </RowSize>
                }
            />
            
            <CollapsableSections 
                title="Cláusulas Particulares"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( particularClauses ) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Vencimiento de mejoras"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( improvementExpiration ) }
                    </RowSize>
                }
            />

            <Modal 
                title="Detalle del articulo asegurado"
                open={ showModal }
                onCancel={() => setShowModal(false)}
                footer=""
                width={800}
                children={
                    <>
                        {
                            objects.map(( object, key ) => _renderCard( key, object ) )
                        }
                    </>
                }
            />
        </>
    )
}